import { useEffect, useRef, useCallback, useState } from 'react';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { executeParallelSave } from '../utils/parallelSaveUtils';
import { useAuth } from './useAuth';
import { useSaveStatus } from '../contexts/SaveStatusContext';

interface AutoSaveOptions {
  enabled?: boolean;
  intervalMs?: number;
}

export const useAutoSave = (options: AutoSaveOptions = {}) => {
  const { enabled = true, intervalMs = 30000 } = options; // 30 seconds default
  const { user } = useAuth();
  const { saveStatus } = useSaveStatus(); // Use SaveStatusIndicator as source of truth
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastSaveAttemptRef = useRef<number>(0);
  
  const { state } = useWhiteboardStore();
  const { canvasId, canvasName } = state;

  const AUTO_SAVE_INTERVAL = intervalMs;
  const AUTO_SAVE_COOLDOWN = 5000; // 5 seconds cooldown
  const [lastAutoSaveAttempt, setLastAutoSaveAttempt] = useState<number>(0);

  const saveCanvas = useCallback(async () => {
    if (!canvasId || !canvasName || !user) {
      return;
    }

    if (saveStatus !== 'tainted') {
      return;
    }

    const now = Date.now();
    if (now - lastAutoSaveAttempt < AUTO_SAVE_COOLDOWN) {
      return;
    }

    try {
      await executeParallelSave({
        userId: user.uid,
        canvasId,
        canvasName,
        description: state.description,
        tags: state.tags
      });
      setLastAutoSaveAttempt(now);
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  }, [canvasId, canvasName, user, saveStatus, state.description, state.tags, lastAutoSaveAttempt]);

  const checkAndAutoSave = useCallback(async () => {
    if (!canvasId || !canvasName || !user) {
      return;
    }

    if (saveStatus !== 'tainted') {
      return;
    }

    const now = Date.now();
    if (now - lastAutoSaveAttempt < AUTO_SAVE_COOLDOWN) {
      return;
    }

    try {
      await saveCanvas();
      setLastAutoSaveAttempt(now);
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  }, [canvasId, canvasName, user, saveStatus, lastAutoSaveAttempt, saveCanvas]);

  useEffect(() => {
    if (canvasId && canvasName && user) {
      const interval = setInterval(checkAndAutoSave, AUTO_SAVE_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [canvasId, canvasName, user, checkAndAutoSave]);

  useEffect(() => {
    if (canvasId && canvasName && user) {
      checkAndAutoSave();
    }
  }, [saveStatus, canvasId, canvasName, user, checkAndAutoSave]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return {
    startAutoSave: () => {
      if (!enabled || intervalRef.current) return;
      intervalRef.current = setInterval(checkAndAutoSave, AUTO_SAVE_INTERVAL);
    },
    stopAutoSave: () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    },
    performAutoSave: checkAndAutoSave
  };
};
